import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import logo from "../../assets/images/logo.png";
import { useTranslation } from "../../contexts/TranslationContext";

import "../../assets/css/landing.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

export default function Navbar() {
  const [activeLink, setActiveLink] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, toggleLanguage, language, isRTL } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Mark <body> as a landing page so the global resets/fonts in landing.css
  // (which is bundled globally) apply only while a landing page is mounted,
  // not on the admin/partner portals.
  useEffect(() => {
    document.body.classList.add("landing-page");
    return () => document.body.classList.remove("landing-page");
  }, []);

  // Turn the transparent home navbar into a solid white sticky bar on scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { name: t("navigation.home"), path: "home" },
    { name: t("navigation.about"), path: "about" },
    { name: t("navigation.services"), path: "services" },
    { name: t("navigation.features"), path: "features" },
    // { name: t("navigation.screenshots"), path: "screenshot" },
    { name: t("navigation.download"), path: "downloadApp" },
  ];

  // Handle mobile navigation click
  const handleMobileNavClick = (path) => {
    setActiveLink(path);
    setIsMobileMenuOpen(false); // Close mobile menu
  };

  // Handle desktop navigation click
  const handleDesktopNavClick = (path) => {
    setActiveLink(path);
  };

  // Handle logo click
  const handleLogoClick = () => {
    setActiveLink("home");
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // On the home page, scroll to the section. On other pages, route back to
  // the home page (with a hash) so the section links still work.
  const renderNavLink = (link, baseClass, onClick) => {
    const className = `${baseClass} ${activeLink === link.path ? "active" : ""}`;
    if (isHome) {
      return (
        <ScrollLink
          to={link.path}
          smooth={true}
          duration={500}
          spy={true}
          offset={-80}
          className={className}
          onClick={onClick}
        >
          {link.name}
        </ScrollLink>
      );
    }
    return (
      <Link
        to={link.path === "home" ? "/" : `/#${link.path}`}
        className={className}
        onClick={onClick}
      >
        {link.name}
      </Link>
    );
  };

  return (
    <>
      {/* Main Navbar */}
      <nav className={`navbar navbar-expand-lg px-5${isHome ? ' navbar-home' : ''}${isHome && isScrolled ? ' scrolled' : ''}`} id="home" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container-fluid d-flex justify-content-between align-items-center">
          {/* Logo */}
          {isHome ? (
            <ScrollLink
              className="navbar-brand"
              to="home"
              smooth={true}
              duration={500}
              spy={true}
              offset={-80}
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
            >
              <img src={logo} alt="Logo" className="logo" />
            </ScrollLink>
          ) : (
            <Link className="navbar-brand" to="/" onClick={handleLogoClick}>
              <img src={logo} alt="Logo" className="logo" />
            </Link>
          )}

          {/* Desktop Navigation */}
          <div className="collapse navbar-collapse justify-content-center d-none d-lg-flex">
            <ul className="navbar-nav gap-4">
              {links.map((link) => (
                <li
                  className="nav-item d-flex flex-column align-items-center"
                  key={link.path}
                >
                  {renderNavLink(link, "nav-link mt-2", () =>
                    handleDesktopNavClick(link.path)
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Language Toggle + Become a Partner (Desktop) */}
          <div className="d-none d-lg-flex align-items-center gap-3">
            <button
              type="button"
              className={`lang-switch ${language === 'ar' ? 'is-ar' : 'is-en'}`}
              onClick={toggleLanguage}
              aria-label="Switch language"
            >
              <span className="lang-switch-knob">{language === 'eng' ? 'EN' : 'AR'}</span>
            </button>
            <Link
              className="btn rounded-2 contact-btn"
              to="/become-partner"
            >
              {t("buttons.becomePartner")}
            </Link>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            className="navbar-toggler d-lg-none"
            type="button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className="navbar-toggler-icon" />
          </button>
        </div>
      </nav>

      {/* Custom Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={closeMobileMenu}
        />
      )}

      {/* Custom Mobile Menu */}
      <div className={`custom-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        {/* Mobile Menu Header */}
        <div className="mobile-menu-header">
          <div className="d-flex align-items-center">
            <img 
              src={logo} 
              alt="LMTKOM Logo" 
              className="logo" 
              style={{ 
                height: '40px', 
                width: 'auto'
              }} 
            />
          </div>
          <button
            type="button"
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Close mobile menu"
          >
            ×
          </button>
        </div>

        {/* Mobile Menu Body */}
        <div className="mobile-menu-body">
          {/* Navigation Links */}
          <ul className={`mobile-nav-list ${isRTL ? 'isRTL' : ''}`}>
            {links.map((link) => (
              <li
                className="mobile-nav-item"
                key={link.path}
              >
                {renderNavLink(link, `mobile-nav-link ${isRTL ? 'isRTL' : ''}`, () =>
                  handleMobileNavClick(link.path)
                )}
              </li>
            ))}
          </ul>

          {/* Language Toggle + Become a Partner (Mobile) */}
          <div className="mobile-menu-footer">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold text-muted">Language</span>
              <button
                type="button"
                className={`lang-switch ${language === 'ar' ? 'is-ar' : 'is-en'}`}
                onClick={toggleLanguage}
                aria-label="Switch language"
              >
                <span className="lang-switch-knob">{language === 'eng' ? 'EN' : 'AR'}</span>
              </button>
            </div>
            <Link
              className="btn rounded-2 contact-btn w-100"
              to="/become-partner"
              onClick={closeMobileMenu}
            >
              {t("buttons.becomePartner")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}